<template>
  <div>
    <q-dialog
      v-model="open"
      class="timeout-dialog"
      :data-cy="DATA_CY.TIMEOUT_DIALOG"
      persistent
    >
      <q-card class="md:w-43.75rem">
        <q-card-section class="row items-center q-pb-none">
          <DialogTitle class="text-weight-bold">
            <span class="text-0.825rem">
              You have been inactive for a period of time and will be logged out
              in
              <span class="text-ba-primary" :data-cy="DATA_CY.TIME">{{
                secondsCounter
              }}</span>
              seconds.
            </span>
          </DialogTitle>
        </q-card-section>
        <q-card-actions class="timeout-dialog-card-actions" align="right">
          <q-btn flat :data-cy="DATA_CY.STAY_LOGGED_IN" @click="stayLoggedIn">
            <span class="text-ba-primary text-0.825rem">{{
              CTA.STAY_LOGGED_IN
            }}</span>
          </q-btn>
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts" setup>
import { EnumerationsLogoutType } from 'client/identity'
import { authTimeoutTimerStart } from '~/constants'
import { CTA, DATA_CY } from '~/constants/strings'
import { useAuthStore } from '~/stores/auth'
import { convertToSeconds } from '~/helpers/date'

const AuthStore = useAuthStore()
const route = useRoute()

const open = ref(false)
const secondsCounter = ref(authTimeoutTimerStart)
const lastActiveTime: Ref<number | null> = ref(null)
let countdown: ReturnType<typeof setInterval> | null = null
// to communicate across tabs
const broadcastChannel = new BroadcastChannel('session_channel')
//title of page in tab
const originalTitle = document.title
let blinkTitleInterval: ReturnType<typeof setInterval> | null = null
const COUNTDOWN_THRESHOLD_SECONDS = 60
const COUNTDOWN_DELAY_SECONDS = 5

const startTitleBlinking = () => {
  blinkTitleInterval = setInterval(() => {
    document.title =
      document.title === 'Session Expiring!'
        ? originalTitle
        : 'Session Expiring!'
  }, 1000)
}

const stopTitleBlinking = () => {
  clearInterval(blinkTitleInterval as unknown as number)
  document.title = originalTitle
}

const clearCountdownInterval = () => {
  if (countdown) {
    clearInterval(countdown as NodeJS.Timeout)
    countdown = null
  }
}

const resetStateAndCounter = () => {
  open.value = false
  secondsCounter.value = authTimeoutTimerStart
  AuthStore.isNearTimeout = false
}

let countdownTimeoutId: number | null = null
const startCountdownWithDelay = (delay: number, seconds: number) => {
  countdownTimeoutId = setTimeout(() => {
    startCountdown(seconds)
    open.value = true
    startTitleBlinking()
  }, delay) as unknown as number
}

watch(
  () => AuthStore.isNearTimeout,
  async (isNearTimeout) => {
    if (isNearTimeout) {
      // Fetch the exact remaining time
      const remainingTimeString =
        (await AuthStore.getRemainingSessionTime()) as `${number}:${number}:${number}`
      const remainingSeconds = convertToSeconds(remainingTimeString)
      await AuthStore.pauseSessionTimeoutWorker()

      if (
        remainingSeconds >
        COUNTDOWN_DELAY_SECONDS + COUNTDOWN_THRESHOLD_SECONDS
      ) {
        // Calculate the milliseconds until the 60-second mark
        const delayMilliseconds =
          (remainingSeconds -
            (COUNTDOWN_DELAY_SECONDS + COUNTDOWN_THRESHOLD_SECONDS)) *
          1000

        startCountdownWithDelay(delayMilliseconds, COUNTDOWN_THRESHOLD_SECONDS)
      } else {
        // If already less than 60 seconds, start the countdown immediately
        startCountdown(remainingSeconds - COUNTDOWN_DELAY_SECONDS)
        open.value = true
        startTitleBlinking()
      }
    } else {
      stopTitleBlinking()
    }
  }
)

const startCountdown = (remainingSeconds: number) => {
  secondsCounter.value = remainingSeconds

  countdown = setInterval(() => {
    secondsCounter.value--
    if (secondsCounter.value <= 0) {
      clearCountdownInterval()
      handleLogOut()
    }
  }, 1000) // Decrement every second
}

const performLogout = async () => {
  if (route.meta.requiresAuth) {
    await AuthStore.logout(EnumerationsLogoutType.Timeout)
  }
  resetStateAndCounter()
  stopTitleBlinking()
}

const handleLogOut = async () => {
  await performLogout()
  broadcastChannel.postMessage({
    type: 'logout',
  })
}

const stayLoggedIn = async () => {
  clearCountdownInterval()
  lastActiveTime.value = Date.now()
  // tell other open tabs to stay logged in
  broadcastChannel.postMessage({
    type: 'stayLoggedIn',
    time: lastActiveTime.value,
  })

  await AuthStore.getLoggedInUserData()
  AuthStore.resumeSessionTimeoutWorker()
  resetStateAndCounter()
  stopTitleBlinking()
}

onMounted(() => {
  broadcastChannel.addEventListener('message', handleBroadcastMessage)
})

onUnmounted(() => {
  broadcastChannel.removeEventListener('message', handleBroadcastMessage)
  broadcastChannel.close()
  clearCountdownInterval()
  stopTitleBlinking()
  if (countdownTimeoutId !== null) {
    clearTimeout(countdownTimeoutId)
  }
})

// handles communication between tabs
const handleBroadcastMessage = (event: MessageEvent) => {
  if (event.data.type === 'stayLoggedIn') {
    lastActiveTime.value = event.data.time
    resetStateAndCounter()
    stopTitleBlinking()
  } else if (event.data.type === 'logout') {
    performLogout()
  }
}
</script>
